import { DragEvent, useRef, useState } from 'react';
import { Glyphicon } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import UploadIcon from 'components/icons/Upload.svg?react';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import useNotify from 'hooks/useNotify';
import useUploadAgencyHeroMutation from '../useUploadAgencyHeroMutation';
import {
  HeroDropzoneStyled,
  HeroFieldStyled,
  HeroPlaceholderStyled,
  HeroPreviewStyled,
  HeroUploadButtonWrapper,
} from './HeroImage.styles';

const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/png',
];

const HeroImageField = ({ heroUrl }: { heroUrl?: string | null }) => {
  const { t } = useTranslation();
  const { notifyError } = useNotify();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadHero, isLoading } = useUploadAgencyHeroMutation();

  const handleFile = (file?: File) => {
    if (!file) {
      return;
    }

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      notifyError(
        t('pageDirectBookingEngine.websiteSettings.heroInvalidFileType'),
      );
      return;
    }

    uploadHero(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const openFilePicker = () => fileInputRef.current?.click();

  return (
    <HeroFieldStyled>
      <HeroDropzoneStyled
        $isDragging={isDragging}
        data-testid="hero-image-dropzone"
        onClick={openFilePicker}
        onDragLeave={() => setIsDragging(false)}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDrop={handleDrop}
      >
        {isLoading && <LoadingPlaceholder width="150px" linesCount={8.9} />}
        {!isLoading && heroUrl && (
          <HeroPreviewStyled
            alt=""
            data-testid="hero-image-preview"
            src={heroUrl}
          />
        )}
        {!isLoading && !heroUrl && (
          <HeroPlaceholderStyled>
            <Glyphicon glyph="picture" />
            {t('pageDirectBookingEngine.websiteSettings.heroPlaceholder')}
          </HeroPlaceholderStyled>
        )}
      </HeroDropzoneStyled>

      <input
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        aria-hidden="true"
        tabIndex={-1}
        data-testid="hero-image-input"
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = '';
        }}
        ref={fileInputRef}
        type="file"
      />

      <HeroUploadButtonWrapper>
        <Button
          data-testid="hero-image-upload-button"
          disabled={isLoading}
          onClick={openFilePicker}
        >
          <UploadIcon />
          {t('common.upload')}
        </Button>
      </HeroUploadButtonWrapper>
    </HeroFieldStyled>
  );
};

HeroImageField.defaultProps = {
  heroUrl: undefined,
};

export default HeroImageField;
